/**
 * Michelangelo Phase 2: Hospital Capability Profile Service
 * Manages hospital_capability_profiles (IPCAF answers, derived capabilities, compliance surface).
 */

import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';

const COLLECTION = 'hospital_capability_profiles';

function removeUndefined(obj) {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(removeUndefined);
  if (typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = removeUndefined(v);
  }
  return out;
}

class HospitalCapabilityProfileService {
  constructor() {
    this.collectionRef = collection(db, COLLECTION);
  }

  async getForOrganization(organizationId) {
    const q = query(
      this.collectionRef,
      where('organizationId', '==', organizationId)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  }

  async get(profileId) {
    const ref = doc(db, COLLECTION, profileId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  async createOrUpdate(organizationId, data, userId, userName) {
    const existing = await this.getForOrganization(organizationId);
    const now = Timestamp.now();
    const payload = removeUndefined({
      organizationId,
      hospitalId: data.hospitalId || organizationId,
      instruments: data.instruments || [],
      capabilities: data.capabilities || {},
      complianceSurface: data.complianceSurface || null,
      lastUpdated: now,
      lastUpdatedBy: userId,
      version: (existing?.version || 0) + (data.incrementVersion ? 1 : 0),
    });

    if (existing) {
      await updateDoc(doc(db, COLLECTION, existing.id), payload);
      return existing.id;
    }
    const newRef = doc(collection(db, COLLECTION));
    await setDoc(newRef, {
      ...payload,
      createdAt: now,
      createdBy: userId,
    });
    return newRef.id;
  }

  async submitIPCAF(organizationId, answers, userId, userName, instrumentId = 'IPCAF_2019') {
    const assessmentInstrumentService = (await import('./assessmentInstrumentService')).default;
    const michelangeloEngineService = (await import('./michelangeloEngineService')).default;
    const instrument = await assessmentInstrumentService.getInstrument(instrumentId);
    if (!instrument) throw new Error(`Instrument ${instrumentId} not found`);

    const { derivedCapabilities, totalScore, maxPossibleScore, componentScores, maturityLevel } =
      michelangeloEngineService.computeIPCAFResults(instrument, answers);

    const newEntry = {
      instrumentId,
      instrumentName: instrument.name || instrument.nameRo,
      domain: instrument.domain,
      completedAt: Timestamp.now(),
      completedBy: userId,
      completedByName: userName || null,
      dueForReassessment: Timestamp.fromDate(
        new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      ),
      totalScore,
      maxPossibleScore,
      scorePercentage: maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0,
      maturityLevel,
      componentScores,
      answers,
      derivedCapabilities,
    };
    const existing = await this.getForOrganization(organizationId);
    const instruments = existing?.instruments || [];
    const updatedInstruments = [...instruments, newEntry];
    const mergedCapabilities = michelangeloEngineService.mergeCapabilities(
      existing?.capabilities || {},
      derivedCapabilities
    );
    const complianceSurface = await michelangeloEngineService.computeComplianceSurface(
      mergedCapabilities,
      instrument.domain
    );

    await this.createOrUpdate(
      organizationId,
      {
        instruments: updatedInstruments,
        capabilities: mergedCapabilities,
        complianceSurface,
        incrementVersion: true,
      },
      userId,
      userName
    );

    return { maturityLevel, totalScore, complianceSurface };
  }

  /**
   * Submit a scored WHO assessment (HHSAF, PSA)
   * Computes scores from component-based questions with scored options
   */
  async submitScoredAssessment(organizationId, answers, userId, userName, instrumentId) {
    const assessmentInstrumentService = (await import('./assessmentInstrumentService')).default;
    const instrument = await assessmentInstrumentService.getInstrument(instrumentId);
    if (!instrument) throw new Error(`Instrument ${instrumentId} not found`);

    const componentScores = [];
    let totalScore = 0;
    let maxPossibleScore = 0;

    for (const comp of instrument.components || []) {
      let compScore = 0;
      let compMax = 0;
      for (const q of comp.questions || []) {
        const ans = answers[q.id];
        const opt = (q.options || []).find((o) => o.value === ans);
        const score = opt?.score ?? opt?.value ?? 0;
        compScore += score;
        const maxScore = Math.max(...(q.options || []).map((o) => o.score ?? o.value ?? 0), 0);
        compMax += maxScore;
      }
      componentScores.push({
        componentId: comp.id,
        componentCode: comp.code,
        componentName: comp.nameEn || comp.nameRo,
        score: compScore,
        maxScore: compMax,
      });
      totalScore += compScore;
      maxPossibleScore += compMax;
    }

    // Determine maturity level from instrument.maturityLevels
    let maturityLevel = null;
    if (instrument.maturityLevels) {
      for (const level of instrument.maturityLevels) {
        if (totalScore >= level.minScore && totalScore <= level.maxScore) {
          maturityLevel = level.level;
          break;
        }
      }
    }

    const existing = await this.getForOrganization(organizationId);
    const instruments = existing?.instruments || [];
    const newEntry = {
      instrumentId,
      instrumentName: instrument.name || instrument.nameRo || instrument.nameEn,
      domain: instrument.domain,
      completedAt: Timestamp.now(),
      completedBy: userId,
      completedByName: userName || null,
      dueForReassessment: Timestamp.fromDate(
        new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      ),
      totalScore,
      maxPossibleScore,
      scorePercentage: maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0,
      maturityLevel,
      componentScores,
      answers,
    };
    const updatedInstruments = [...instruments, newEntry];

    await this.createOrUpdate(
      organizationId,
      {
        instruments: updatedInstruments,
        incrementVersion: true,
      },
      userId,
      userName
    );

    return { maturityLevel, totalScore, maxPossibleScore, scorePercentage: newEntry.scorePercentage };
  }

  /**
   * Submit a checklist WHO assessment (SSC, SCC)
   * Tracks completion percentage per phase
   */
  async submitChecklistAssessment(organizationId, answers, userId, userName, instrumentId) {
    const assessmentInstrumentService = (await import('./assessmentInstrumentService')).default;
    const instrument = await assessmentInstrumentService.getInstrument(instrumentId);
    if (!instrument) throw new Error(`Instrument ${instrumentId} not found`);

    const phaseCompletions = [];
    let totalItems = 0;
    let completedItems = 0;

    for (const phase of instrument.phases || []) {
      let phaseTotal = (phase.items || []).length;
      let phaseCompleted = 0;
      for (const item of phase.items || []) {
        if (answers[item.id] !== undefined && answers[item.id] !== null && answers[item.id] !== '') {
          phaseCompleted++;
        }
      }
      phaseCompletions.push({
        phaseId: phase.id,
        phaseCode: phase.code,
        phaseName: phase.nameEn || phase.nameRo,
        totalItems: phaseTotal,
        completedItems: phaseCompleted,
        completionPercentage: phaseTotal > 0 ? (phaseCompleted / phaseTotal) * 100 : 0,
      });
      totalItems += phaseTotal;
      completedItems += phaseCompleted;
    }

    const completionPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

    const existing = await this.getForOrganization(organizationId);
    const instruments = existing?.instruments || [];
    const newEntry = {
      instrumentId,
      instrumentName: instrument.name || instrument.nameRo || instrument.nameEn,
      domain: instrument.domain,
      completedAt: Timestamp.now(),
      completedBy: userId,
      completedByName: userName || null,
      dueForReassessment: Timestamp.fromDate(
        new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      ),
      totalItems,
      completedItems,
      completionPercentage,
      phaseCompletions,
      answers,
    };
    const updatedInstruments = [...instruments, newEntry];

    await this.createOrUpdate(
      organizationId,
      {
        instruments: updatedInstruments,
        incrementVersion: true,
      },
      userId,
      userName
    );

    return { completionPercentage, totalItems, completedItems, phaseCompletions };
  }

  /**
   * Get assessment run history for an instrument (from hospital_capability_profiles).
   * Returns runs sorted by completedAt descending. No delete; view-only.
   */
  async getAssessmentHistory(organizationId, instrumentId) {
    const profile = await this.getForOrganization(organizationId);
    const instruments = profile?.instruments || [];
    const runs = instruments
      .filter((i) => i.instrumentId === instrumentId)
      .map((i) => ({
        ...i,
        completedAt: i.completedAt?.toDate?.() ?? (i.completedAt instanceof Date ? i.completedAt : null),
      }))
      .sort((a, b) => {
        const ta = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const tb = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return tb - ta;
      });
    return runs;
  }
}

export default new HospitalCapabilityProfileService();
